import { Button, SxProps, Unstable_Grid2 as Grid, useTheme } from "@mui/material";
import { ReactElement } from "react";
import { ChartLegendItem } from "src/components/monitoring/commonTypes";
import { SenecBaseTheme } from "src/styles/senecBaseTheme";
import { unstyledButtonStyle } from "src/components/monitoring/senec-chart-legend/SenecChartLegendUtils";
import { LegendItem } from "src/components/monitoring/senec-chart-legend/SenecChartLegendItem";
import { formatLegendValue } from "src/components/monitoring/chartUtils";
import { useScreenSize } from "src/hooks/useScreenSize";

export type SenecChartLegendProps = {
    /**
     * An array of items that represent each legend entry.
     */
    chartLegendItems: ChartLegendItem[];
    /**
     * An array containing the selected legend types.
     */
    selectedLegendTypes: string[];
    /**
     * Number of legend item columns
     */
    columns?: number;
    /**
     * Number of grid columns a legend item takes up
     */
    legendItemColumns?: number;
    /**
     * Direction of the legend items
     * @default "row"
     */
    direction?: "row" | "column";
    /**
     * Function to toggle the selection of a legend type.
     */
    toggleSelectedLegendTypes?(legendType: string): void;
    /**
     * A flag to indicate if the component is in a loading state.
     */
    isLoading?: boolean;
    /**
     * Locale object for localization.
     */
    locale: Intl.Locale;
    /**
     * If set to true "undefined" legend values will be displayed with the
     * placeholder string "-"
     */
    showNoValueIndicator?: boolean;
    /**
     * Style overrides for the chart legend grid container
     */
    sx?: SxProps;
};

export const SenecChartLegend = ({
    chartLegendItems,
    selectedLegendTypes,
    columns = 8,
    legendItemColumns,
    toggleSelectedLegendTypes,
    isLoading,
    locale,
    showNoValueIndicator = false,
    direction = "row",
    sx,
}: SenecChartLegendProps): ReactElement => {
    const theme: SenecBaseTheme = useTheme();
    const { isSmallDesktop, isDesktop } = useScreenSize();
    const isBigValue = checkForBigValues(chartLegendItems, locale);
    const isInteractive = Boolean(toggleSelectedLegendTypes);

    return (
        <Grid
            data-testid={"chart-legend"}
            container
            columns={columns}
            spacing={{ xsMobile: 1, xs: 2 }}
            justifyContent={"space-between"}
            width={"100%"}
            direction={direction}
            sx={sx}
        >
            {chartLegendItems.map((item) => {
                const legendItem = (
                    <LegendItem
                        chartLegendItem={item}
                        selectedLegendTypes={selectedLegendTypes}
                        isLoading={isLoading}
                        locale={locale}
                        showNoValueIndicator={showNoValueIndicator as boolean}
                        isBigValue={isBigValue}
                        linePosition={direction === "row" ? "top" : "left"}
                    />
                );

                return (
                    <Grid
                        data-testid={"chart-legend-item-" + item.id}
                        key={item.id}
                        xsMobile={legendItemColumns ?? 2}
                        md={legendItemColumns ?? 1}
                        display={"grid"}
                        alignContent={"center"}
                        height="100%"
                    >
                        {isInteractive ? (
                            <Button
                                key={item.id}
                                data-testid={"chart-legend-button-" + item.id}
                                aria-label={"legend button " + item.translation}
                                onClick={() => toggleSelectedLegendTypes!(item.id)}
                                sx={{
                                    ...unstyledButtonStyle,
                                    cursor: "pointer",
                                    width: "100%",
                                    display: "block",
                                    borderRadius: "0 0 6px 6px",
                                    "&:focus, &:hover": {
                                        background: theme.palette.neutralGray000.main,
                                        borderRadius: "0 0 6px 6px",
                                        color: theme.palette.black.main,
                                    },
                                    height: "100%",
                                    minWidth: isSmallDesktop || isDesktop ? "77px" : "auto",
                                }}
                            >
                                {legendItem}
                            </Button>
                        ) : (
                            legendItem
                        )}
                    </Grid>
                );
            })}
        </Grid>
    );
};

export function checkForBigValues(items: ChartLegendItem[], locale: Intl.Locale): boolean {
    return items.some(
        (item) => formatLegendValue(locale.toString(), item.legendValue, undefined, true, item.dataSeriesUnit).length > 5,
    );
}
